import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type AdminPlaceChangeView, type AdminPlaceView, type AdminPlacesView } from '@vello/api-client';
import {
  Badge,
  Button,
  DateRangeInput,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  EmptyState,
  Field,
  ListTable,
  Notice,
  PageHeader,
  RadioGroup,
  Checkbox,
  Row,
  SectionTitle,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  type Column,
  type TimelineEntry,
} from '@vello/web-theme';
import {
  DEFAULT_PLACES_SORT,
  EMPTY_PLACES_FILTERS,
  PLACES_PAGE_SIZE,
  PLACE_SORT_OF,
  PLACE_STATUSES,
  PURGE_CHOICES,
  draftOfPlace,
  formatCoordinate,
  formatMarketInstant,
  mayClearRouteCache,
  newPlaceDraft,
  placeDraftIssue,
  placeInputOf,
  placePatchOf,
  placeStatusOf,
  placeTimeZone,
  placesAuthority,
  placesFilterTimeZone,
  placesFiltersActive,
  placesQuery,
  placesVisibleColumns,
  pendingCountQuery,
  purgeCountQuery,
  purgeNeedsSource,
  purgeNeedsWord,
  purgeRequestOf,
  retentionText,
  screenAllowed,
  toggledSort,
  type PlaceColumnKey,
  type PlaceDraft,
  type PlacesFilters,
  type PlacesSort,
  type PurgeChoice,
  type PurgeOptions,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { DetailRow } from '../components/detail-row';
import { DrawerStack } from '../components/drawer-stack';
import { FormDialog } from '../components/form-dialog';
import { HistoryTimeline } from '../components/history-timeline';
import { IdRef } from '../components/code-ref';
import { PlaceForm, PlaceMap, usePlaceMapConfig, usePostalLabel, vocabularyCodes } from '../components/place-form';
import { useDebounced } from '../components/use-debounced';
import { PlaceFeedbackPanel } from './PlaceFeedbackPanel';

/**
 * Operações › Endereços (D674, doc 27): the shared base of addresses and named places, in the shape of the owner's DevBase "Lista de Endereços" - a filter under every column,
 * sortable headers, the date and time of each row always on screen - with manual create / edit / hide / delete and the bulk clears.
 *
 * The scope selector at the top is the ONE geography control: the list reads the scope (a city, a state, a country or the platform), the country and state columns are drawn
 * only where the scope does not already pin them, and there is no second country select. A row opens in the right-hand drawer (the point and the pickup point on the map,
 * every field, the change history); "Editar" turns the drawer into the form (tap the map to place the point). "Limpar endereços" clears by scope, by source or only the expired,
 * after a confirmation that says how many go (the whole scope also asks for a word typed); "Limpar cache de rotas" clears the paid route answers. The retention rule is not
 * edited here: its button opens the governed settings on `geo.places`. Every code on screen is the central catalog's word; every change is audited by the server.
 */

type Banner = { readonly tone: 'success' | 'danger'; readonly text: string };

type DrawerState =
  | { readonly mode: 'create' }
  | { readonly mode: 'view' | 'edit'; readonly id: string };

export function PlacesScreen(): ReactElement {
  const { runtime, scope, grants, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, explain, fail } = useVocab();
  const place = usePlaceNames();
  const { confirm, toast } = useConsole();
  const { navigate } = useRouter();
  const authority = placesAuthority(grants);
  const current = scope.current;
  const scopeZone = placesFilterTimeZone(current, markets);

  // ---- the list ---------------------------------------------------------------------------------------------------------------------------

  const [filters, setFilters] = useState<PlacesFilters>(EMPTY_PLACES_FILTERS);
  const settled = useDebounced(filters, 350);
  const [sort, setSort] = useState<PlacesSort>(DEFAULT_PLACES_SORT);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<AdminPlacesView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [banner, setBanner] = useState<Banner | undefined>(undefined);
  const readSeq = useRef(0);
  const scopeKey = `${current.country ?? ''}|${current.region ?? ''}|${current.city ?? ''}`;

  const refuse = useCallback(
    (e: { readonly code: string; readonly reasonCode?: string | undefined; readonly params?: Readonly<Record<string, string | number>> | undefined }): string => explain(e) ?? t('places.error', { code: fail(e) }),
    [explain, fail, t],
  );

  // A new scope, filter or sort starts again from the first page.
  useEffect(() => {
    setOffset(0);
  }, [scopeKey, settled, sort]);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    const res = await runtime.places.list(placesQuery(current, settled, sort, offset, scopeZone));
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setData(res.value);
      setError(undefined);
    } else {
      // A refused read keeps the list unknown: the screen says it could not load, never "no places".
      setError(refuse(res.error));
    }
  }, [runtime, current, settled, sort, offset, scopeZone, refuse]);

  useEffect(() => {
    void load();
  }, [load]);

  const operators = data?.operators ?? {};
  const operatorName = (id: string | null): string => (id === null ? t('places.operator.system') : operators[id] ?? t('places.operator.unknown'));

  // ---- columns ----------------------------------------------------------------------------------------------------------------------------

  const sources = useMemo(() => vocabularyCodes('place.source'), []);
  const kinds = useMemo(() => vocabularyCodes('place.kind'), []);
  const countries = useMemo(() => [...new Set(markets.map((m) => m.countryCode.toUpperCase()))].sort((a, b) => place.country(a).localeCompare(place.country(b))), [markets, place]);
  const { regions } = useAdmin();
  const filterStates = useMemo(() => {
    const country = filters.country !== '' ? filters.country : current.country?.toUpperCase();
    return (regions ?? []).filter((r) => r.status === 'ACTIVE' && (country === undefined || r.countryCode.toUpperCase() === country));
  }, [regions, filters.country, current.country]);

  const sortOf = (key: PlaceColumnKey, header: string): Column<AdminPlaceView>['sort'] => {
    const column = PLACE_SORT_OF[key];
    if (column === undefined) return undefined;
    const direction = sort.column === column ? sort.direction : undefined;
    return {
      direction,
      onToggle: () => setSort((s) => toggledSort(s, column)),
      label: t(direction === 'asc' ? 'places.sort.asc' : direction === 'desc' ? 'places.sort.desc' : 'places.sort.none', { column: header }),
    };
  };
  const set = (patch: Partial<PlacesFilters>): void => setFilters((f) => ({ ...f, ...patch }));
  const by = (header: string): string => t('places.filter.by', { column: header });
  const text = (key: keyof PlacesFilters, header: string, placeholderId: string): ReactElement => (
    <TextInput value={filters[key]} aria-label={by(header)} placeholder={t(placeholderId)} onChange={(e) => set({ [key]: e.target.value } as Partial<PlacesFilters>)} />
  );
  const yesNo = (key: 'verified', header: string): ReactElement => (
    <Select value={filters[key]} aria-label={by(header)} onChange={(e) => set({ [key]: e.target.value } as Partial<PlacesFilters>)}>
      <option value="">{t('places.filter.all')}</option>
      <option value="true">{t('places.filter.yes')}</option>
      <option value="false">{t('places.filter.no')}</option>
    </Select>
  );

  // The country pack names the postal code ("CEP", "ZIP code"...): the list uses it when the scope pins one city, the drawer when its place or draft has one.
  const listPostal = usePostalLabel(current.city !== undefined && current.city !== '' ? current.city : undefined);
  const columnDef = (key: PlaceColumnKey): Column<AdminPlaceView> => {
    const header = key === 'postalCode' && listPostal !== undefined ? listPostal : t(`places.col.${key}`);
    const sortable = sortOf(key, header);
    const base = { key, header, ...(sortable !== undefined ? { sort: sortable } : {}) };
    switch (key) {
      case 'id':
        return { ...base, width: 'minmax(120px, 0.7fr)', filter: text('id', header, 'places.filter.ph.id'), render: (p) => <IdRef value={p.id} /> };
      case 'createdAt':
        return {
          ...base,
          width: 'minmax(340px, 1.5fr)',
          filter: <DateRangeInput idPrefix="places-created" from={filters.createdFrom} to={filters.createdTo} onFromChange={(day) => set({ createdFrom: day })} onToChange={(day) => set({ createdTo: day })} fromLabel={t('places.filter.from')} toLabel={t('places.filter.to')} />,
          render: (p) => <Text className="tnum">{formatMarketInstant(p.createdAt, placeTimeZone(p, scopeZone, markets), lang, 'datetime')}</Text>,
        };
      case 'postalCode':
        return { ...base, width: 'minmax(140px, 0.8fr)', filter: <TextInput value={filters.postalCode} aria-label={by(header)} placeholder={listPostal ?? t('places.filter.ph.postalCode')} onChange={(e) => set({ postalCode: e.target.value })} />, render: (p) => <Text className="tnum">{p.postalCode ?? '—'}</Text> };
      case 'latitude':
        return { ...base, width: 'minmax(120px, 0.7fr)', render: (p) => <Text className="tnum">{formatCoordinate(p.location.latitude)}</Text> };
      case 'longitude':
        return { ...base, width: 'minmax(120px, 0.7fr)', render: (p) => <Text className="tnum">{formatCoordinate(p.location.longitude)}</Text> };
      case 'name':
        return { ...base, width: 'minmax(170px, 1fr)', render: (p) => <Text as="strong">{p.name ?? '—'}</Text> };
      case 'address':
        return { ...base, width: 'minmax(260px, 1.8fr)', filter: text('q', header, 'places.filter.ph.address'), render: (p) => <span title={p.formattedAddress}>{p.formattedAddress}</span> };
      case 'city':
        return { ...base, width: 'minmax(150px, 1fr)', filter: text('city', header, 'places.filter.ph.city'), render: (p) => <Text>{p.cityName ?? '—'}</Text> };
      case 'state':
        return {
          ...base,
          width: 'minmax(170px, 1fr)',
          filter: (
            <Select value={filters.region} aria-label={by(header)} onChange={(e) => set({ region: e.target.value })}>
              <option value="">{t('places.filter.all')}</option>
              {filterStates.map((r) => (
                <option key={r.code} value={r.code}>{r.name}</option>
              ))}
            </Select>
          ),
          render: (p) => <Text>{p.regionCode === null ? '—' : place.region(p.regionCode)}</Text>,
        };
      case 'country':
        return {
          ...base,
          width: 'minmax(160px, 1fr)',
          filter: (
            <Select value={filters.country} aria-label={by(header)} onChange={(e) => set({ country: e.target.value, region: '' })}>
              <option value="">{t('places.filter.all')}</option>
              {countries.map((c) => (
                <option key={c} value={c}>{place.country(c)}</option>
              ))}
            </Select>
          ),
          render: (p) => <Text>{place.country(p.countryCode)}</Text>,
        };
      case 'kind':
        return {
          ...base,
          width: 'minmax(160px, 1fr)',
          filter: (
            <Select value={filters.kind} aria-label={by(header)} onChange={(e) => set({ kind: e.target.value })}>
              <option value="">{t('places.filter.all')}</option>
              {kinds.map((k) => (
                <option key={k} value={k}>{v('place.kind', k)}</option>
              ))}
            </Select>
          ),
          render: (p) => <Text>{p.kind === null ? '—' : v('place.kind', p.kind)}</Text>,
        };
      case 'source':
        return {
          ...base,
          width: 'minmax(160px, 1fr)',
          filter: (
            <Select value={filters.source} aria-label={by(header)} onChange={(e) => set({ source: e.target.value })}>
              <option value="">{t('places.filter.allF')}</option>
              {sources.map((s) => (
                <option key={s} value={s}>{v('place.source', s)}</option>
              ))}
            </Select>
          ),
          render: (p) => <Text>{v('place.source', p.source)}</Text>,
        };
      case 'useCount':
        return { ...base, numeric: true, width: 'minmax(90px, max-content)', render: (p) => <Text className="tnum">{String(p.useCount)}</Text> };
      case 'verified':
        return { ...base, width: 'minmax(130px, 0.7fr)', filter: yesNo('verified', header), render: (p) => <Badge tone={p.verified ? 'success' : 'neutral'}>{t(p.verified ? 'places.filter.yes' : 'places.filter.no')}</Badge> };
      case 'status':
        return {
          ...base,
          width: 'minmax(150px, 0.8fr)',
          filter: (
            <Select value={filters.status} aria-label={by(header)} onChange={(e) => set({ status: e.target.value as PlacesFilters['status'] })}>
              <option value="">{t('places.filter.allF')}</option>
              {PLACE_STATUSES.map((s) => (
                <option key={s} value={s}>{v('place.status', s)}</option>
              ))}
            </Select>
          ),
          render: (p) => <Badge tone={placeStatusOf(p) === 'visible' ? 'success' : placeStatusOf(p) === 'expired' ? 'warning' : 'neutral'}>{v('place.status', placeStatusOf(p))}</Badge>,
        };
    }
  };
  const visibleColumns = placesVisibleColumns(current);
  const columns = visibleColumns.map(columnDef);
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const shownFrom = total === 0 ? 0 : offset + 1;
  const shownTo = offset + rows.length;

  // ---- the drawer -------------------------------------------------------------------------------------------------------------------------

  const [drawer, setDrawer] = useState<DrawerState | undefined>(undefined);
  const [detail, setDetail] = useState<{ readonly place: AdminPlaceView; readonly changes: readonly AdminPlaceChangeView[]; readonly operators: Readonly<Record<string, string>> } | undefined>(undefined);
  const [historyError, setHistoryError] = useState(false);
  const [draft, setDraft] = useState<PlaceDraft | undefined>(undefined);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drawerNotice, setDrawerNotice] = useState<Banner | undefined>(undefined);

  const openId = drawer !== undefined && drawer.mode !== 'create' ? drawer.id : undefined;
  const openPlace: AdminPlaceView | undefined = openId === undefined ? undefined : detail !== undefined && detail.place.id === openId ? detail.place : rows.find((p) => p.id === openId);
  const mapMarket = drawer?.mode === 'create' || drawer?.mode === 'edit' ? (draft?.marketId !== '' ? draft?.marketId : undefined) ?? current.city : openPlace?.marketId ?? current.city;
  const map = usePlaceMapConfig(mapMarket === '' ? undefined : mapMarket);
  // The drawer reuses the list's label when it is the same city; another city's is read once.
  const scopeCity = current.city !== undefined && current.city !== '' ? current.city : undefined;
  const drawerCity = drawer === undefined || mapMarket === '' ? undefined : mapMarket;
  const otherPostal = usePostalLabel(drawerCity !== scopeCity ? drawerCity : undefined);
  const drawerPostal = drawerCity === undefined ? undefined : drawerCity === scopeCity ? listPostal : otherPostal;

  const readDetail = useCallback(
    async (id: string): Promise<void> => {
      setHistoryError(false);
      const res = await runtime.places.get(id);
      if (res.ok) setDetail(res.value);
      else {
        setDetail(undefined);
        setHistoryError(true);
      }
    },
    [runtime],
  );

  const openRow = (p: AdminPlaceView): void => {
    setDrawer({ mode: 'view', id: p.id });
    setDetail(undefined);
    setDraft(undefined);
    setDirty(false);
    setDrawerNotice(undefined);
    setBanner(undefined);
    void readDetail(p.id);
  };
  const closeDrawer = (): void => {
    setDrawer(undefined);
    setDetail(undefined);
    setDraft(undefined);
    setDirty(false);
    setDrawerNotice(undefined);
  };
  const startCreate = (): void => {
    setDrawer({ mode: 'create' });
    setDetail(undefined);
    setDraft(newPlaceDraft(current, markets));
    setDirty(false);
    setDrawerNotice(undefined);
    setBanner(undefined);
  };
  const startEdit = (p: AdminPlaceView): void => {
    setDrawer({ mode: 'edit', id: p.id });
    setDraft(draftOfPlace(p));
    setDirty(false);
    setDrawerNotice(undefined);
  };
  const stopEdit = (): void => {
    if (drawer?.mode === 'create') closeDrawer();
    else if (openId !== undefined) {
      setDrawer({ mode: 'view', id: openId });
      setDraft(undefined);
      setDirty(false);
      setDrawerNotice(undefined);
    }
  };
  const change = (next: PlaceDraft): void => {
    setDraft(next);
    setDirty(true);
  };

  const issue = draft === undefined ? undefined : placeDraftIssue(draft);
  const patch = drawer?.mode === 'edit' && openPlace !== undefined && draft !== undefined ? placePatchOf(openPlace, draft) : undefined;
  const nothingChanged = patch !== undefined && Object.keys(patch).length === 0;

  const submitCreate = async (): Promise<void> => {
    const input = draft === undefined ? undefined : placeInputOf(draft);
    if (input === undefined) return;
    setBusy(true);
    setDrawerNotice(undefined);
    const res = await runtime.places.create(input);
    setBusy(false);
    if (res.ok) {
      toast({ message: t(res.value.created ? 'places.done.created' : 'places.done.merged'), tone: 'success' });
      closeDrawer();
      await load();
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
  };

  const submitEdit = async (): Promise<void> => {
    if (openPlace === undefined || patch === undefined || nothingChanged) return;
    setBusy(true);
    setDrawerNotice(undefined);
    const res = await runtime.places.update(openPlace.id, patch);
    setBusy(false);
    if (res.ok) {
      toast({ message: t('places.done.updated'), tone: 'success' });
      setDrawer({ mode: 'view', id: openPlace.id });
      setDraft(undefined);
      setDirty(false);
      await readDetail(openPlace.id);
      await load();
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
  };

  const toggleHidden = async (p: AdminPlaceView): Promise<void> => {
    setBusy(true);
    setDrawerNotice(undefined);
    const res = p.hidden ? await runtime.places.unhide(p.id) : await runtime.places.hide(p.id);
    setBusy(false);
    if (res.ok) {
      toast({ message: t(p.hidden ? 'places.done.shown' : 'places.done.hidden'), tone: 'success' });
      await readDetail(p.id);
      await load();
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
  };

  // D678: an alias becomes the official name (the suggester then titles the place with it for everyone). The edit makes the row verified and is audited by the server;
  // a name the place already had is kept as an alias, so nothing the operator typed is lost.
  const useAsName = async (p: AdminPlaceView, alias: string): Promise<void> => {
    setBusy(true);
    setDrawerNotice(undefined);
    const res = await runtime.places.update(p.id, { name: alias, ...(p.name !== null ? { aliases: [...p.aliases.filter((a) => a !== alias && a !== p.name), p.name] } : {}) });
    setBusy(false);
    if (res.ok) {
      toast({ message: t('places.done.updated'), tone: 'success' });
      await readDetail(p.id);
      await load();
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
  };

  const remove = async (p: AdminPlaceView): Promise<void> => {
    const answer = await confirm({ title: t('places.delete.title'), body: t('places.delete.body'), confirmLabel: t('places.delete.confirm'), cancelLabel: t('ui.cancel'), tone: 'danger' });
    if (!answer.confirmed) return;
    setBusy(true);
    setDrawerNotice(undefined);
    const res = await runtime.places.remove(p.id);
    setBusy(false);
    if (res.ok) {
      toast({ message: t('places.done.deleted'), tone: 'success' });
      closeDrawer();
      await load();
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
  };

  const history: readonly TimelineEntry[] | undefined = detail === undefined || openPlace === undefined || detail.place.id !== openPlace.id ? undefined : detail.changes.map((c) => ({
    id: c.id,
    tone: c.action === 'delete' ? ('danger' as const) : c.action === 'hide' ? ('warning' as const) : ('neutral' as const),
    title: v('place.change_action', c.action),
    detail: v('place.reason', c.reasonCode),
    meta: t('places.field.by', { when: formatMarketInstant(c.at, placeTimeZone(openPlace, scopeZone, markets), lang, 'datetime'), who: c.actorId === null ? t('places.operator.system') : detail.operators[c.actorId] ?? t('places.operator.unknown') }),
  }));

  // ---- the bulk clears --------------------------------------------------------------------------------------------------------------------

  const scopeLabel = current.city !== undefined && current.city !== '' ? place.market(current.city) : current.region !== undefined && current.region !== '' ? place.region(current.region) : current.country !== undefined && current.country !== '' ? place.country(current.country) : t('places.purge.scope.global');
  const [purging, setPurging] = useState<PurgeOptions | undefined>(undefined);
  const [purgeCount, setPurgeCount] = useState<number | 'unknown' | undefined>(undefined);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState<string | undefined>(undefined);
  const [cacheMode, setCacheMode] = useState<'all' | 'expired' | undefined>(undefined);

  const purgeKey = purging === undefined ? '' : `${purging.choice}|${purging.source}|${String(purging.keepVerified)}|${scopeKey}`;
  useEffect(() => {
    if (purging === undefined) return undefined;
    if (purgeNeedsSource(purging.choice) && purging.source === '') {
      setPurgeCount(undefined);
      return undefined;
    }
    let live = true;
    setPurgeCount(undefined);
    void runtime.places.list(purgeCountQuery(current, purging)).then((res) => {
      if (live) setPurgeCount(res.ok ? res.value.total : 'unknown');
    });
    return () => {
      live = false;
    };
    // purgeKey carries every input of the count
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purgeKey, runtime]);

  const countText = (count: number | 'unknown' | undefined): string | undefined =>
    count === undefined ? undefined : count === 'unknown' ? t('places.purge.countUnknown') : count === 0 ? t('places.purge.countNone') : count === 1 ? t('places.purge.countOne') : t('places.purge.count', { count });

  const runPurge = async (options: PurgeOptions): Promise<void> => {
    const request = purgeRequestOf(current, options);
    if (request === undefined) return;
    setPurgeBusy(true);
    setPurgeError(undefined);
    const res = await runtime.places.purge(request);
    setPurgeBusy(false);
    if (res.ok) {
      setPurging(undefined);
      toast({ message: res.value.removed === 0 ? t('places.purge.doneNone') : res.value.removed === 1 ? t('places.purge.doneOne') : t('places.purge.done', { count: res.value.removed }), tone: 'success' });
      closeDrawer();
      await load();
    } else setPurgeError(refuse(res.error));
  };

  const confirmPurge = async (): Promise<void> => {
    if (purging === undefined) return;
    const word = t('places.purge.word');
    const known = typeof purgeCount === 'number';
    const answer = await confirm({
      title: t('places.purge.confirmTitle'),
      body: !known ? t('places.purge.confirmBodyUnknown', { scope: scopeLabel }) : purgeCount === 1 ? t('places.purge.confirmBodyOne', { scope: scopeLabel }) : t('places.purge.confirmBody', { count: purgeCount, scope: scopeLabel }),
      confirmLabel: t('places.purge.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: 'danger',
      ...(purgeNeedsWord(purging.choice) ? { reasonLabel: t('places.purge.wordLabel', { word }), notePlaceholder: word, requireText: word } : {}),
    });
    if (answer.confirmed) await runPurge(purging);
  };

  const runCache = async (mode: 'all' | 'expired'): Promise<void> => {
    setPurgeBusy(true);
    setPurgeError(undefined);
    const res = await runtime.places.purgeRouteCache(mode);
    setPurgeBusy(false);
    if (res.ok) {
      setCacheMode(undefined);
      toast({ message: res.value.removed === 0 ? t('places.cache.doneNone') : res.value.removed === 1 ? t('places.cache.doneOne') : t('places.cache.done', { count: res.value.removed }), tone: 'success' });
    } else setPurgeError(refuse(res.error));
  };

  const confirmCache = async (): Promise<void> => {
    if (cacheMode === undefined) return;
    const answer = await confirm({ title: t('places.cache.confirmTitle'), body: t('places.cache.confirmBody'), confirmLabel: t('places.purge.confirm'), cancelLabel: t('ui.cancel'), tone: 'danger' });
    if (answer.confirmed) await runCache(cacheMode);
  };

  // ---- the retention line (read-only) -------------------------------------------------------------------------------------------------------

  const retention = data?.retention;
  const days = (n: number | null): string => (n === null ? t('places.retention.none') : t('places.retention.days', { days: n }));
  const retentionLine = ((): string => {
    if (retention === undefined) return t('places.retention.pickCountry');
    const r = retentionText(retention);
    const base = t('places.retention.line', { value: days(r.days) });
    return r.overrides.length === 0 ? base : `${base}. ${t('places.retention.overrides', { list: r.overrides.map((o) => t('places.retention.override', { source: v('place.source', o.source), value: days(o.days) })).join(', ') })}`;
  })();

  // ---- render ---------------------------------------------------------------------------------------------------------------------------

  const clearOpen = (): void => {
    setPurgeError(undefined);
    setPurging({ choice: 'all', source: '', keepVerified: false });
  };
  const emptyState = placesFiltersActive(filters)
    ? <EmptyState title={t('places.emptyFiltered.title')} detail={t('places.emptyFiltered.detail')} />
    : <EmptyState title={t('places.empty.title')} detail={t('places.empty.detail')} />;

  // ---- D704 (doc 28 gap G6): the Sugestões tab - the riders' place feedback queue, with the scope's pending count on the tab ------------------------------------

  const [tab, setTab] = useState<'places' | 'feedback'>('places');
  const [pending, setPending] = useState<number | undefined>(undefined);
  const readPending = useCallback(async (): Promise<void> => {
    const res = await runtime.placeFeedback.list(pendingCountQuery(current));
    setPending(res.ok ? res.value.total : undefined);
  }, [runtime, current]);
  useEffect(() => {
    if (authority.mayView) void readPending();
  }, [readPending, authority.mayView]);

  const editing = drawer !== undefined && drawer.mode !== 'view';
  const drawerTitle = drawer?.mode === 'create' ? t('places.form.title.new') : drawer?.mode === 'edit' ? t('places.form.title.edit') : openPlace === undefined ? '' : openPlace.name ?? openPlace.formattedAddress;

  return (
    <Stack gap={5}>
      <PageHeader
        title={t('places.title')}
        subtitle={t('places.subtitle')}
        {...(authority.mayManage && tab === 'places' ? { actions: <Button variant="primary" onClick={startCreate}>{t('places.new')}</Button> } : {})}
      />

      <Tabs
        id="places"
        ariaLabel={t('places.tabs')}
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'places', label: t('places.tab.places') },
          { id: 'feedback', label: t('places.tab.feedback'), ...(pending !== undefined ? { count: String(pending) } : {}) },
        ]}
      />

      {tab === 'feedback' ? (
        <Stack gap={4} role="tabpanel" id="places-panel-feedback" aria-labelledby="places-tab-feedback">
          <PlaceFeedbackPanel onDecided={() => void readPending()} />
        </Stack>
      ) : (
      <Stack gap={5} role="tabpanel" id="places-panel-places" aria-labelledby="places-tab-places">
      {!authority.mayManage && <Notice tone="neutral">{t('places.readOnly')}</Notice>}
      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {banner !== undefined && <Notice tone={banner.tone}>{banner.text}</Notice>}

      <Row gap={3} align="center" wrap>
        <Text role="label" tone="secondary">{retentionLine}</Text>
        {screenAllowed(grants, 'governed_settings') && <Button variant="secondary" size="sm" onClick={() => navigate('governed_settings', {}, { key: 'geo.places' })}>{t('places.retention.open')}</Button>}
        <Text role="caption" tone="secondary" className="tnum">{t('places.total', { total })}</Text>
        {placesFiltersActive(filters) && <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY_PLACES_FILTERS)}>{t('places.filters.clear')}</Button>}
        {authority.mayPurge && <Button variant="secondary" size="sm" onClick={clearOpen}>{t('places.clear.open')}</Button>}
        {mayClearRouteCache(current, authority) && <Button variant="secondary" size="sm" onClick={() => { setPurgeError(undefined); setCacheMode('expired'); }}>{t('places.clear.cache')}</Button>}
      </Row>

      <ListTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id}
        onRowClick={openRow}
        {...(openId !== undefined ? { selectedKey: openId } : {})}
        loading={data === undefined && error === undefined}
        loadingLabel={t('ui.loading')}
        error={error !== undefined ? t('places.failed') : undefined}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={emptyState}
        minWidth={Math.max(1100, visibleColumns.length * 175)}
        pagination={{
          from: shownFrom,
          to: shownTo,
          total,
          hasPrev: offset > 0,
          hasNext: offset + PLACES_PAGE_SIZE < total,
          onPrev: () => setOffset((o) => Math.max(0, o - PLACES_PAGE_SIZE)),
          onNext: () => setOffset((o) => o + PLACES_PAGE_SIZE),
          prevLabel: t('dir.prev'),
          nextLabel: t('dir.next'),
          rangeLabel: t('dir.range', { from: String(shownFrom), to: String(shownTo), total: String(total) }),
        }}
      />

      <Drawer open={drawer !== undefined} onClose={() => (busy ? undefined : closeDrawer())} labelledBy="places-drawer-title">
        {drawer !== undefined && (editing ? draft !== undefined : openPlace !== undefined) && (
          <>
            <DrawerHeader
              titleId="places-drawer-title"
              title={drawerTitle}
              {...(drawer.mode === 'view' && openPlace !== undefined ? { subtitle: <Badge tone={placeStatusOf(openPlace) === 'visible' ? 'success' : placeStatusOf(openPlace) === 'expired' ? 'warning' : 'neutral'}>{v('place.status', placeStatusOf(openPlace))}</Badge> } : {})}
              onClose={closeDrawer}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
                {drawerNotice !== undefined && <Notice tone={drawerNotice.tone}>{drawerNotice.text}</Notice>}
                {editing && draft !== undefined ? (
                  <PlaceForm
                    idPrefix="place"
                    draft={draft}
                    onChange={change}
                    disabled={busy}
                    editing={drawer.mode === 'edit'}
                    tiles={map.tiles}
                    center={map.center}
                    issue={dirty ? issue : undefined}
                    postalLabel={drawerPostal}
                  />
                ) : (
                  openPlace !== undefined && (
                    <>
                      <Stack gap={2}>
                        <SectionTitle>{t('places.section.map')}</SectionTitle>
                        <PlaceMap point={openPlace.location} pickup={openPlace.pickupPoint} tiles={map.tiles} center={map.center} />
                      </Stack>

                      <Stack gap={1}>
                        <SectionTitle>{t('places.section.details')}</SectionTitle>
                        <DetailRow label={t('places.field.name')} value={openPlace.name ?? '—'} mono={false} />
                        {authority.mayManage && openPlace.aliases.length > 0 ? (
                          <DetailRow label={t('places.field.aliases')} mono={false}>
                            <Stack gap={1}>
                              {openPlace.aliases.map((a) => (
                                <Row key={a} gap={2} align="center" wrap>
                                  <Text role="body">{a}</Text>
                                  <Button variant="ghost" disabled={busy} aria-label={t('places.alias.useAsNameFor', { alias: a })} onClick={() => void useAsName(openPlace, a)}>{t('places.alias.useAsName')}</Button>
                                </Row>
                              ))}
                            </Stack>
                          </DetailRow>
                        ) : (
                          <DetailRow label={t('places.field.aliases')} value={openPlace.aliases.length === 0 ? '—' : openPlace.aliases.join(', ')} mono={false} />
                        )}
                        <DetailRow label={t('places.field.address')} value={openPlace.formattedAddress} mono={false} />
                        <DetailRow label={t('places.field.street')} value={[openPlace.street, openPlace.houseNumber].filter((x): x is string => x !== null).join(', ') || '—'} mono={false} />
                        <DetailRow label={t('places.field.neighborhood')} value={openPlace.neighborhood ?? '—'} mono={false} />
                        <DetailRow label={t('places.field.complement')} value={openPlace.complement ?? '—'} mono={false} />
                        <DetailRow label={t('places.field.city')} value={openPlace.cityName ?? '—'} mono={false} />
                        <DetailRow label={t('places.field.market')} value={openPlace.marketId === null ? '—' : place.market(openPlace.marketId)} mono={false} />
                        <DetailRow label={t('places.field.state')} value={openPlace.regionCode === null ? '—' : place.region(openPlace.regionCode)} mono={false} />
                        <DetailRow label={t('places.field.country')} value={place.country(openPlace.countryCode)} mono={false} />
                        <DetailRow label={drawerPostal ?? t('places.field.postalCode')} value={openPlace.postalCode ?? '—'} />
                        <DetailRow label={t('places.field.kind')} value={openPlace.kind === null ? '—' : v('place.kind', openPlace.kind)} mono={false} />
                        <DetailRow label={t('places.field.precision')} value={v('place.precision', openPlace.precision)} mono={false} />
                        <DetailRow label={t('places.field.point')} value={`${formatCoordinate(openPlace.location.latitude)}, ${formatCoordinate(openPlace.location.longitude)}`} mono />
                        <DetailRow label={t('places.field.pickup')} value={openPlace.pickupPoint === null ? '—' : `${formatCoordinate(openPlace.pickupPoint.latitude)}, ${formatCoordinate(openPlace.pickupPoint.longitude)}`} mono />
                        <DetailRow label={t('places.field.source')} value={v('place.source', openPlace.source)} mono={false} />
                        <DetailRow label={t('places.field.uses')} value={String(openPlace.useCount)} mono />
                        <DetailRow label={t('places.field.lastUsed')} value={openPlace.lastUsedAt === null ? '—' : formatMarketInstant(openPlace.lastUsedAt, placeTimeZone(openPlace, scopeZone, markets), lang, 'datetime')} mono />
                        <DetailRow label={t('places.field.verified')} value={t(openPlace.verified ? 'places.filter.yes' : 'places.filter.no')} mono={false} />
                        <DetailRow label={t('places.field.status')} value={v('place.status', placeStatusOf(openPlace))} mono={false} />
                        <DetailRow label={t('places.field.expires')} value={openPlace.expiresAt === null ? t('places.field.neverExpires') : formatMarketInstant(openPlace.expiresAt, placeTimeZone(openPlace, scopeZone, markets), lang, 'datetime')} mono={openPlace.expiresAt !== null} />
                        <DetailRow label={t('places.field.created')} value={t('places.field.by', { when: formatMarketInstant(openPlace.createdAt, placeTimeZone(openPlace, scopeZone, markets), lang, 'datetime'), who: operatorName(openPlace.createdBy) })} mono={false} />
                        <DetailRow label={t('places.field.updated')} value={t('places.field.by', { when: formatMarketInstant(openPlace.updatedAt, placeTimeZone(openPlace, scopeZone, markets), lang, 'datetime'), who: operatorName(openPlace.updatedBy) })} mono={false} />
                        <DetailRow label={t('places.field.id')}><IdRef value={openPlace.id} copy full /></DetailRow>
                      </Stack>

                      <Stack gap={2}>
                        <SectionTitle>{t('places.section.history')}</SectionTitle>
                        <HistoryTimeline entries={history} error={historyError ? t('places.history.failed') : undefined} onRetry={() => void readDetail(openPlace.id)} emptyTitle={t('places.history.empty')} />
                      </Stack>
                    </>
                  )
                )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              {editing ? (
                <>
                  <Button variant="ghost" disabled={busy} onClick={stopEdit}>{t('places.form.cancel')}</Button>
                  {drawer.mode === 'create' ? (
                    <Button variant="primary" disabled={busy || issue !== undefined} onClick={() => void submitCreate()}>{t('places.form.create')}</Button>
                  ) : (
                    <Button variant="primary" disabled={busy || issue !== undefined || patch === undefined || nothingChanged} onClick={() => void submitEdit()}>{t('places.form.save')}</Button>
                  )}
                </>
              ) : (
                openPlace !== undefined && (
                  <>
                    <Button variant="ghost" onClick={closeDrawer}>{t('places.drawer.close')}</Button>
                    {authority.mayManage && (
                      <>
                        <Button variant="danger" disabled={busy} onClick={() => void remove(openPlace)}>{t('places.delete')}</Button>
                        <Button variant="secondary" disabled={busy} onClick={() => void toggleHidden(openPlace)}>{t(openPlace.hidden ? 'places.show' : 'places.hide')}</Button>
                        <Button variant="primary" disabled={busy} onClick={() => startEdit(openPlace)}>{t('places.edit')}</Button>
                      </>
                    )}
                  </>
                )
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>

      <FormDialog open={purging !== undefined} id="places-purge" icon="ban" tone="danger" title={t('places.purge.title')} body={t('places.purge.body')} busy={purgeBusy} onCancel={() => setPurging(undefined)}>
        {purging !== undefined && (
          <Stack gap={3}>
            <RadioGroup
              name="places-purge-choice"
              ariaLabel={t('places.purge.choices')}
              value={purging.choice}
              onChange={(choice) => setPurging({ ...purging, choice: choice as PurgeChoice })}
              options={PURGE_CHOICES.map((c) => ({ value: c, label: c === 'all' ? t('places.purge.all', { scope: scopeLabel }) : t(`places.purge.${c}`) }))}
            />
            {purgeNeedsSource(purging.choice) && (
              <Field label={t('places.purge.sourceField')} htmlFor="places-purge-source">
                <Select id="places-purge-source" value={purging.source} onChange={(e) => setPurging({ ...purging, source: e.target.value })}>
                  <option value="">{t('places.purge.sourceChoose')}</option>
                  {sources.map((s) => (
                    <option key={s} value={s}>{v('place.source', s)}</option>
                  ))}
                </Select>
              </Field>
            )}
            <Checkbox checked={purging.keepVerified} onChange={(keepVerified) => setPurging({ ...purging, keepVerified })} label={t('places.purge.keep')} />
            {countText(purgeCount) !== undefined && <Text role="label" tone={purgeCount === 'unknown' ? 'secondary' : 'primary'} aria-live="polite">{countText(purgeCount)}</Text>}
            {purgeError !== undefined && <Notice tone="danger">{purgeError}</Notice>}
            <Row gap={2} justify="end">
              <Button variant="ghost" disabled={purgeBusy} onClick={() => setPurging(undefined)}>{t('ui.cancel')}</Button>
              <Button variant="danger-solid" disabled={purgeBusy || purgeCount === undefined || purgeCount === 0} onClick={() => void confirmPurge()}>{t('places.purge.next')}</Button>
            </Row>
          </Stack>
        )}
      </FormDialog>

      <FormDialog open={cacheMode !== undefined} id="places-cache" icon="ban" tone="danger" title={t('places.cache.title')} body={t('places.cache.body')} busy={purgeBusy} onCancel={() => setCacheMode(undefined)}>
        {cacheMode !== undefined && (
          <Stack gap={3}>
            <RadioGroup
              name="places-cache-mode"
              ariaLabel={t('places.cache.choices')}
              value={cacheMode}
              onChange={(m) => setCacheMode(m as 'all' | 'expired')}
              options={[{ value: 'expired', label: t('places.cache.expired') }, { value: 'all', label: t('places.cache.all') }]}
            />
            {purgeError !== undefined && <Notice tone="danger">{purgeError}</Notice>}
            <Row gap={2} justify="end">
              <Button variant="ghost" disabled={purgeBusy} onClick={() => setCacheMode(undefined)}>{t('ui.cancel')}</Button>
              <Button variant="danger-solid" disabled={purgeBusy} onClick={() => void confirmCache()}>{t('places.purge.next')}</Button>
            </Row>
          </Stack>
        )}
      </FormDialog>
      </Stack>
      )}
    </Stack>
  );
}
